import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Card, Label, TextInput } from 'flowbite-react';
import { FormEvent, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import type { DashboardContext } from 'src/routes/dashboard/container';

export function DashboardIntegrations() {
  const { store, klaviyoAccount, shopifyShop } = useOutletContext<DashboardContext>();

  if (!store) {
    return null;
  }

  const klaviyoAccountOrganizationName =
    store.key_name == 'maverickdemo' ? 'My Klaviyo Account' : klaviyoAccount?.organization_name;
  const myShopifyDomain =
    store.key_name == 'maverickdemo' ? 'my-store.myshopify.com' : shopifyShop?.myshopify_domain;
  const klaviyoAccountAccessScopeIsCurrent =
    store.key_name == 'maverickdemo'
      ? false
      : klaviyoAccount
        ? klaviyoAccount.access_scope_is_current
        : true;
  const shopifyShopAccessScopeIsCurrent =
    store.key_name == 'maverickdemo'
      ? false
      : shopifyShop
        ? shopifyShop.access_scope_is_current
        : true;
  const klaviyoAccountApiKeyDisplay =
    store.key_name == 'maverickdemo' ? '*****9999' : klaviyoAccount?.display_api_key;
  const klaviyoErrorMessage = store.key_name == 'maverickdemo' ? '' : klaviyoAccount?.error_message;
  const shopifyErrorMessage = store.key_name == 'maverickdemo' ? '' : shopifyShop?.error_message;

  return (
    <>
      <div>
        {shopifyShopAccessScopeIsCurrent || (
          <Alert color='failure' className='mb-4'>
            <span>
              <FontAwesomeIcon icon={solid('circle-info')} className='h-4 w-4 flex-shrink-0' />
            </span>
            <span className='sr-only'>Info</span>
            <span className='ml-3 text-sm font-medium'>
              Your Shopify token is out of date. Click &quot;Update Shopify&quot; below to
              reauthorize the Maverick app.
            </span>
          </Alert>
        )}
        {klaviyoAccountAccessScopeIsCurrent || (
          <Alert color='failure' className='mb-4'>
            <span>
              <FontAwesomeIcon icon={solid('circle-info')} className='h-4 w-4 flex-shrink-0' />
            </span>
            <span className='sr-only'>Info</span>
            <span className='ml-3 text-sm font-medium'>
              We now support Klaviyo OAUTH! Click &quot;Update Klaviyo&quot; below to reauthorize
              the Maverick app. {klaviyoAccount?.access_scope_is_current}
            </span>
          </Alert>
        )}
        <div className='mb-6 grid gap-6 md:grid-cols-2'>
          <ShopifyIntegration
            myShopifyDomain={myShopifyDomain}
            storeKeyName={store.key_name}
            accessScopeIsCurrent={shopifyShopAccessScopeIsCurrent}
            shopifyErrorMessage={shopifyErrorMessage}
          />
          <KlaviyoIntegration
            organizationName={klaviyoAccountOrganizationName}
            storeKeyName={store.key_name}
            accessScopeIsCurrent={klaviyoAccountAccessScopeIsCurrent}
            klaviyoErrorMessage={klaviyoErrorMessage}
            apiKeyDisplay={klaviyoAccountApiKeyDisplay}
          />
        </div>
      </div>
    </>
  );
}

export function ShopifyIntegration({
  myShopifyDomain,
  storeKeyName,
  accessScopeIsCurrent,
  shopifyErrorMessage,
}: {
  myShopifyDomain: string | null | undefined;
  storeKeyName: string;
  accessScopeIsCurrent: boolean;
  shopifyErrorMessage: string | null | undefined;
}) {
  const [shopifyStoreInputValue, setShopifyStoreKeyInputValue] = useState('');
  const [hasNewShopifyStore, setHasNewShopifyStore] = useState(false);

  const connectShopifyStore = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // maverick-test-store.myshopify.com
    const shop = myShopifyDomain
      ? myShopifyDomain.split('.')[0]
      : shopifyStoreInputValue.split('.')[0];
    // window.location.href = 'https://maverick.ngrok.io/accounts/shopify/login/?process=connect&shop=' + shop;
    const after_login = '/app/' + storeKeyName + '/integrations/';
    setHasNewShopifyStore(true);
    window.location.href =
      'https://themavericklab.com/accounts/shopify/login/?process=connect&shop=' +
      shop +
      '&next=' +
      after_login;
  };

  return (
    <Card>
      <div className='flex h-full w-full flex-col'>
        <div className='mb-16 flex w-full'>
          <img
            className='h-16  w-auto max-w-xs'
            src='https://uploads-ssl.webflow.com/61dfc899a471635beadca9b9/61e0cc25bed1e425811842f4_shopify-logo.svg'
            alt='Shopify Logo'
          />
        </div>
        <div className='relative h-full w-full pb-6'>
          {!hasNewShopifyStore && (shopifyErrorMessage ?? !myShopifyDomain) && (
            <>
              <div className='flow-root'>
                <form onSubmit={connectShopifyStore}>
                  <div className='mb-3'>
                    <Label
                      color={shopifyErrorMessage ? 'failure' : 'gray'}
                      htmlFor='shopify-store-url-input'
                      className='text-md font-medium text-gray-900'
                    >
                      Shopify Store URL
                    </Label>
                    <TextInput
                      type='text'
                      color={shopifyErrorMessage ? 'failure' : 'gray'}
                      sizing='sm'
                      id='shopify-store-url-input'
                      helperText={
                        shopifyErrorMessage ? (
                          <>
                            <span className='font-medium'>Oops!</span> {shopifyErrorMessage}
                          </>
                        ) : (
                          ''
                        )
                      }
                      placeholder='Your Shopify Store URL'
                      value={shopifyStoreInputValue}
                      onChange={(e) => setShopifyStoreKeyInputValue(e.target.value)}
                      required
                    ></TextInput>
                  </div>
                  <p id='shopify-helper-text-explanation' className='mt-3 text-sm text-gray-500'>
                    Something like your-brand.myshopify.com.
                  </p>
                  <button
                    type='submit'
                    className='mt-6 w-full rounded-lg bg-blue-700 px-5 py-2.5 text-center text-sm font-medium text-white hover:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-300 sm:w-auto'
                  >
                    Link Shopify
                  </button>
                </form>
              </div>
              <div className='absolute bottom-0 right-0 flow-root'>
                <span className='float-right rounded bg-red-100 px-2.5 py-1 text-xs font-medium text-red-800'>
                  Not connected
                </span>
              </div>
            </>
          )}
          {!shopifyErrorMessage && myShopifyDomain && accessScopeIsCurrent && (
            <>
              <p className='text-md mb-6 font-medium text-gray-900'>Shopify Store URL</p>
              <a href={'https://' + myShopifyDomain} className='text-xl font-light text-blue-600'>
                {myShopifyDomain}
              </a>
              <div className='absolute bottom-0 right-0 flow-root'>
                <span className='float-right rounded bg-green-100 px-2.5 py-1 text-xs font-medium text-green-800'>
                  Connected
                </span>
              </div>
            </>
          )}
          {!shopifyErrorMessage && myShopifyDomain && !accessScopeIsCurrent && (
            <>
              <p className='text-md mb-6 font-medium text-gray-900'>Shopify Store URL</p>
              <a href={'https://' + myShopifyDomain} className='text-xl font-light text-blue-600'>
                {myShopifyDomain}
              </a>
              <form onSubmit={connectShopifyStore}>
                <button
                  type='submit'
                  className='mt-6 w-full rounded-lg bg-blue-700 px-5 py-2.5 text-center text-sm font-medium text-white hover:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-300 sm:w-auto'
                >
                  Update Shopify
                </button>
              </form>
            </>
          )}
          {hasNewShopifyStore && (
            <>
              <p className='text-md mb-6 font-medium text-gray-900'>Shopify Store URL</p>
              <p className='text-xl font-light text-gray-900'>Redirecting to Shopify...</p>
              <div className='absolute bottom-0 right-0 flow-root'>
                <span className='float-right rounded bg-green-100 px-2.5 py-1 text-xs font-medium text-green-800'>
                  Connected
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

export function KlaviyoIntegration({
  organizationName,
  storeKeyName,
  accessScopeIsCurrent,
  klaviyoErrorMessage,
  apiKeyDisplay,
}: {
  organizationName: string | null | undefined;
  storeKeyName: string;
  accessScopeIsCurrent: boolean;
  klaviyoErrorMessage: string | null | undefined;
  apiKeyDisplay: string | null | undefined;
}) {
  const [hasNewKlaviyoAccount, setHasNewKlaviyoAccount] = useState(false);

  const connectKlaviyoAccount = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const after_login = '/app/' + storeKeyName + '/integrations/';
    setHasNewKlaviyoAccount(true);
    // window.location.href =
    //   'http://127.0.0.1:8000/accounts/klaviyo_oauth2/login/?process=connect&next=' + after_login;
    window.location.href =
      'https://themavericklab.com/accounts/klaviyo_oauth2/login/?process=connect&next=' +
      after_login;
  };

  return (
    <Card>
      <div className='flex h-full w-full flex-col'>
        <div className='mb-16 flex w-full'>
          <img
            className='h-16 w-auto max-w-xs'
            src='https://uploads-ssl.webflow.com/61dfc899a471635beadca9b9/61e0cb3b3d3724863d2c4bf3_klaviyo-logo.svg'
            alt='Klaviyo Logo'
          ></img>
        </div>
        <div className='relative h-full w-full pb-6'>
          {!hasNewKlaviyoAccount && (klaviyoErrorMessage ?? !organizationName) && (
            <>
              <div className='flow-root'>
                <form onSubmit={connectKlaviyoAccount}>
                  <button
                    type='submit'
                    className='mt-6 w-full rounded-lg bg-blue-700 px-5 py-2.5 text-center text-sm font-medium text-white hover:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-300 sm:w-auto'
                  >
                    Link Klaviyo
                  </button>
                </form>
              </div>
              <div className='absolute bottom-0 right-0 flow-root'>
                <span className='float-right rounded bg-red-100 px-2.5 py-1 text-xs font-medium text-red-800'>
                  Not connected
                </span>
              </div>
            </>
          )}
          {!hasNewKlaviyoAccount &&
            !klaviyoErrorMessage &&
            organizationName &&
            accessScopeIsCurrent && (
              <>
                <p className='text-md mb-6 font-medium text-gray-900'>Klaviyo Account</p>
                <a
                  href={'https://www.klaviyo.com/dashboard'}
                  className='text-xl font-light text-blue-600'
                >
                  {organizationName}
                </a>
                {apiKeyDisplay && (
                  <p className='text-xl font-light text-gray-900'>{apiKeyDisplay}</p>
                )}
                <div className='absolute bottom-0 right-0 flow-root'>
                  <span className='float-right rounded bg-green-100 px-2.5 py-1 text-xs font-medium text-green-800'>
                    Connected
                  </span>
                </div>
              </>
            )}
          {!hasNewKlaviyoAccount &&
            !klaviyoErrorMessage &&
            organizationName &&
            !accessScopeIsCurrent && (
              <>
                <p className='text-md mb-6 font-medium text-gray-900'>Klaviyo Account</p>
                <a
                  href={'https://www.klaviyo.com/dashboard'}
                  className='text-xl font-light text-blue-600'
                >
                  {organizationName}
                </a>
                {apiKeyDisplay && (
                  <p className='text-xl font-light text-gray-900'>{apiKeyDisplay}</p>
                )}
                <form onSubmit={connectKlaviyoAccount}>
                  <button
                    type='submit'
                    className='mt-6 w-full rounded-lg bg-blue-700 px-5 py-2.5 text-center text-sm font-medium text-white hover:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-300 sm:w-auto'
                  >
                    Update Klaviyo
                  </button>
                </form>
              </>
            )}
          {hasNewKlaviyoAccount && (
            <>
              <p className='text-md mb-6 font-medium text-gray-900'>Klaviyo Account</p>
              <p className='text-xl font-light text-gray-900'>Redirecting to Klaviyo...</p>
              <div className='absolute bottom-0 right-0 flow-root'>
                <span className='float-right rounded bg-green-100 px-2.5 py-1 text-xs font-medium text-green-800'>
                  Connected
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}
